import {
  ContentContainer,
  ToastsProvider,
  TourGuideProvider,
} from "@twinkltech/curriculum-architect-component-library";
import { DomainCacheProvider } from "@twinkltech/curriculum-architect-domain-hooks";
import { TwinklAnalyticsProvider } from "@twinkltech/web-analytics-ts";
import { Outlet, createBrowserRouter } from "react-router-dom";

import { RouteGuard } from "@/components/RouteGuard/RouteGuard";

import { HelpActionsProvider } from "@/contexts/HelpActions/HelpActions.provider";

import { CurriculumListContainer } from "@/pages/CurriculumList/CurriculumList.container";
import { EditSubjectPlanContainer } from "@/pages/EditSubjectPlan/EditSubjectPlan.container";
import { MainLayoutContainer } from "@/pages/MainLayout/MainLayout.container";
import { SubjectListContainer } from "@/pages/SubjectList/SubjectList.container";

import { paths } from "@/paths";

import { GenericError } from "./components/GenericError/GenericError";
import { CurriculumGoalsContainer } from "./pages/CurriculumGoals/CurriculumGoals.container";
import { ExploreSchemeContainer } from "./pages/ExploreScheme/ExploreScheme.container";
import { ExploreSubjectListContentsContainer } from "./pages/ExploreSubjectListContents/ExploreSubjectListContents.container";
import { LearningSequenceContainer } from "./pages/LearningSequence/LearningSequence.container";
import { SchemeOverviewContainer } from "./pages/SchemeOverview/SchemeOverview.container";
import { SubjectPlanOverviewContainer } from "./pages/SubjectPlanOverview/SubjectPlanOverview.container";
import { FeedbackProvider } from "./providers/Feedback/Feedback.provider";

export const router = createBrowserRouter(
  [
    {
      id: "home",
      path: paths.HOME_PATH,
      element: (
        <TwinklAnalyticsProvider>
          <FeedbackProvider>
            <DomainCacheProvider>
              <ToastsProvider>
                <TourGuideProvider overlayZIndex={20}>
                  <HelpActionsProvider>
                    <MainLayoutContainer>
                      <RouteGuard>
                        <Outlet />
                      </RouteGuard>
                    </MainLayoutContainer>
                  </HelpActionsProvider>
                </TourGuideProvider>
              </ToastsProvider>
            </DomainCacheProvider>
          </FeedbackProvider>
        </TwinklAnalyticsProvider>
      ),
      children: [
        {
          id: "curriculum-list",
          path: paths.CURRICULUM_LIST_PATH,
          element: (
            <ContentContainer style={{ paddingLeft: 0 }} topPadding="none">
              <CurriculumListContainer />
            </ContentContainer>
          ),
        },
        {
          id: "curriculum-subject-list",
          path: paths.CURRICULUM_SUBJECT_LIST_PATH,
          element: <SubjectListContainer />,
        },
        {
          id: "subject-plan-edit",
          path: paths.SUBJECT_PLAN_EDIT_PATH,
          element: <EditSubjectPlanContainer />,
        },
        {
          id: "subject-plan-overview",
          path: paths.SUBJECT_PLAN_OVERVIEW_PATH,
          element: <SubjectPlanOverviewContainer />,
        },
        {
          id: "scheme-overview",
          path: paths.SCHEME_PATH_SUPPORTING_MATERIALS,
          element: <SchemeOverviewContainer />,
        },
        {
          id: "scheme-learning-sequence",
          path: paths.SCHEME_LEARNING_SEQUENCE_PATH,
          element: <LearningSequenceContainer />,
        },
        {
          id: "explore-all-subject-contents",
          path: paths.EXPLORE_SCHEME_LIST_CONTENTS_PATH,
          element: <ExploreSubjectListContentsContainer />,
        },
        {
          id: "explore-subject-schemes",
          path: paths.EXPLORE_SUBJECT_CONTENTS_SCHEME_PATH,
          element: <ExploreSchemeContainer />,
        },
        ...(import.meta.env.VITE_ENABLE_DESIGN_PRINCIPLES === "true"
          ? [
              {
                id: "curriculum-goals",
                path: paths.CURRICULUM_GOAL_PATH,
                element: <CurriculumGoalsContainer />,
              },
            ]
          : []),
        {
          id: "not-found",
          path: "*",
          element: <GenericError />,
        },
      ],
    },
  ],
  {
    basename: import.meta.env.VITE_BASE_WEB_APP_PATH || paths.BASE_WEB_APP_PATH,
  }
);
